import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  EAMB_OVERLAY_ATTR,
  lockBodyScroll,
  releaseBodyScrollLock,
} from '../utils/modalOverlay';

interface RegisterModalOverlayProps {
  isOpen: boolean;
  /** Só deve ser chamado por botão X/Cancelar (não por clique no backdrop). */
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Backdrop do cadastro — portal no body + cleanup ao fechar/desmontar.
 * NÃO fecha ao clicar fora: evita abandono acidental com dados preenchidos.
 */
export function RegisterModalOverlay({ isOpen, onClose, children }: RegisterModalOverlayProps) {
  useEffect(() => {
    if (!isOpen) {
      releaseBodyScrollLock();
      return;
    }
    lockBodyScroll();
    return () => releaseBodyScrollLock();
  }, [isOpen]);

  // Esc: não fecha sozinho — usuário usa X/Cancelar (com confirmação no formulário)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-xl animate-in fade-in overflow-y-auto py-4"
      {...{ [EAMB_OVERLAY_ATTR]: 'register' }}
      role="dialog"
      aria-modal="true"
      aria-label="Cadastro"
    >
      <div className="w-full max-w-md m-4 max-h-[95dvh] overflow-y-auto">{children}</div>
    </div>,
    document.body
  );
}
